import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Link,
  Grid,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useNO2Report, type MailDataStructure, type OperatorData, type MailItem } from './hooks';
import Table from '../../../common/components/Table/Table';
import type { SortConfig, SortDirection } from '../../../common/components/Table/types';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import {
  MainContent,
  ReportContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton,
} from './NO2Report.styled';

interface ServiceTypeState {
  aircraftPriority: boolean;
  salNonPriority: boolean;
  surfaceNonPriority: boolean;
  surfacePriority: boolean;
}

interface DespatchState {
  letters: boolean;
  flats: boolean;
  packets: boolean;
  pdfManual: boolean;
  journals: boolean;
  bilateral: boolean;
}

interface AccountingTypeState {
  gross: boolean;
  net: boolean;
}

interface DirectionOption {
  id: string;
  label: string;
}

// Utility function to compare year-quarter combinations
const compareYearQuarter = (year1: string, quarter1: string, year2: string, quarter2: string): number => {
  const y1 = parseInt(year1);
  const y2 = parseInt(year2);
  const q1 = parseInt(quarter1.replace('Q', ''));
  const q2 = parseInt(quarter2.replace('Q', ''));
  
  if (y1 !== y2) {
    return y1 - y2; // Compare years first
  }
  return q1 - q2; // If years are equal, compare quarters
};

// Move mailColumns outside component and memoize it
const createMailColumns = () => [
  { accessor: 'outboundOe', Header: 'OE From' },
  { accessor: 'inboundOe', Header: 'OE To' },
  { accessor: 'rcLabel', Header: 'Rec. Cat.' },
  { accessor: 'gross', Header: 'Gross', textAlign: 'right' },
  { accessor: 'tare', Header: 'Tare', textAlign: 'right' },
  { accessor: 'net', Header: 'Net', textAlign: 'right' },
  { accessor: 'pkg', Header: 'P Net', textAlign: 'right' },
  { accessor: 'gkg', Header: 'G Net', textAlign: 'right' },
  { accessor: 'ekg', Header: 'E Net', textAlign: 'right' },
  { accessor: 'pitems', Header: 'P Items', textAlign: 'right' },
  { accessor: 'gitems', Header: 'G Items', textAlign: 'right' },
  { accessor: 'eitems', Header: 'E Items', textAlign: 'right' },
  { accessor: 'receivedReceptacleStratum', Header: '#Rec.', textAlign: 'right' },
  { accessor: 'numberOfSamples', Header: '#Samples', textAlign: 'right' },
  { accessor: 'effectiveSamplingRateStratum', Header: 'Eff. SR.', textAlign: 'right' },
  { accessor: 'netIpk', Header: 'Net IPK', textAlign: 'right' },
];

// Memoized Results Section Component
const ResultsSection = React.memo<{
  htmlResults: MailDataStructure;
  mailColumns: any[];
  direction: string;
}>(({ htmlResults, mailColumns, direction }) => {
  return (
    <Box sx={{ mt: 6 }}>
      {/* Main Results Title */}
      <Box
        sx={{
          borderBottom: '2px solid #E1E8ED',
          pb: 2,
          mb: 4,
        }}
      >
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            color: '#063142',
            fontSize: '24px',
            letterSpacing: '-0.5px',
          }}
        >
          Report Results
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: '#666',
            mt: 0.5,
            fontSize: '14px',
          }}
        >
          Generated NO2 report data tables
        </Typography>
      </Box>

      {htmlResults.map((operatorData: OperatorData, operatorIndex: number) => (
        <OperatorSection
          key={`${operatorData.operator}-${operatorIndex}`}
          operatorData={operatorData}
          operatorIndex={operatorIndex}
          mailColumns={mailColumns}
          direction={direction}
        />
      ))}
    </Box>
  );
});

// Memoized Operator Section Component
const OperatorSection = React.memo<{
  operatorData: OperatorData;
  operatorIndex: number;
  mailColumns: any[];
  direction: string;
}>(({ operatorData, operatorIndex, mailColumns, direction }) => {
  return (
    <Box
      sx={{
        mb: 6,
        p: 3,
        border: '1px solid #E1E8ED',
        borderRadius: '8px',
        backgroundColor: '#FAFBFC',
      }}
    >
      {/* Operator Title */}
      <Box
        sx={{
          mb: 3,
          pb: 2,
          borderBottom: '1px solid #E1E8ED',
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontWeight: 600,
            color: '#063142',
            fontSize: '20px',
            letterSpacing: '-0.25px',
          }}
        >
          Operator: {operatorData.operator}
        </Typography>
      </Box>

      {operatorData.items.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: '#FFFFFF',
            borderRadius: '6px',
            border: '1px dashed #D1D9E0',
          }}
        >
          <Typography
            sx={{
              fontStyle: 'italic',
              color: '#666',
              fontSize: '14px',
            }}
          >
            No data available for this operator
          </Typography>
        </Box>
      ) : (
        operatorData.items.map((item: MailItem, itemIndex: number) => (
          <MailItemSection
            key={`${item.serviceType}-${item.mailFormat}-${item.accountingType}-${itemIndex}`}
            item={item}
            itemIndex={itemIndex}
            isLastItem={itemIndex === operatorData.items.length - 1}
            mailColumns={mailColumns}
            direction={direction}
          />
        ))
      )}
    </Box>
  );
});

// Memoized Mail Item Section Component
const MailItemSection = React.memo<{
  item: MailItem;
  itemIndex: number;
  isLastItem: boolean;
  mailColumns: any[];
  direction: string;
}>(({ item, itemIndex, isLastItem, mailColumns, direction }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);

  function parseNumeric(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[\'`,\s]/g, '');
      const num = Number(cleaned);
      if (Number.isFinite(num)) return num;
    }
    return null;
  }

  const sortedRows = useMemo(() => {
    const rows = item.rows;
    if (!rows || rows.length === 0) return rows;

    // Keep the last row (totals) fixed at the bottom
    const totalRow = rows[rows.length - 1];
    const bodyRows = rows.slice(0, rows.length - 1);

    if (!sortConfig) return [...bodyRows, totalRow];

    const { column, direction } = sortConfig;
    const multiplier = direction === 'ASC' ? 1 : -1;
    const sortedBody = [...bodyRows].sort((a: any, b: any) => {
      const av = a?.[column];
      const bv = b?.[column];
      const an = parseNumeric(av);
      const bn = parseNumeric(bv);

      if (av == null && bv == null) return 0;
      if (av == null) return -1 * multiplier;
      if (bv == null) return 1 * multiplier;

      if (an !== null && bn !== null) return (an - bn) * multiplier;

      const astr = String(av);
      const bstr = String(bv);
      return astr.localeCompare(bstr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });

    return [...sortedBody, totalRow];
  }, [item.rows, sortConfig]);

  const handleSort = useCallback((column: string, direction: SortDirection | undefined, _searchColumn?: string, _searchTerm?: string) => {
    if (!direction) {
      setSortConfig(undefined);
      return;
    }
    setSortConfig({ column, direction });
  }, []);

  return (
    <Box
      sx={{
        mb: !isLastItem ? 4 : 0,
        p: 3,
        backgroundColor: '#FFFFFF',
        borderRadius: '6px',
        border: '1px solid #E1E8ED',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Table Header Info */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: '#F8F9FA',
          borderRadius: '4px',
          border: '1px solid #E9ECEF',
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            color: '#063142',
            fontSize: '16px',
            mb: 1,
          }}
        >
          Service Type: {item.serviceType} | Mail Format: {item.mailFormat} | Accounting Type:{' '}
          {item.accountingType}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: '#6C757D',
            fontSize: '12px',
            fontFamily: 'monospace',
            backgroundColor: '#FFFFFF',
            padding: '4px 8px',
            borderRadius: '3px',
            border: '1px solid #DEE2E6',
            display: 'inline-block',
          }}
        >
          📄 {item.fileName}
        </Typography>
      </Box>

      {item.rows.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: '#FFF8E1',
            borderRadius: '4px',
            border: '1px dashed #FFE082',
          }}
        >
          <Typography
            sx={{
              fontStyle: 'italic',
              color: '#F57F17',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            ⚠️ No rows available for this item
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            '& .MuiPaper-root': {
              boxShadow: 'none',
              border: '1px solid #E1E8ED',
            },
          }}
        >
          <Table
            data={sortedRows}
            columns={mailColumns}
            moduleName={``}
            showPagination={false}
            viewMode={true}
            disableAutoSizing={false}
            enableSearch={false}
            pageSize={25}
            lazyLoad={item.rows.length > 50}
            lazyLoadHeight={400}
            lazyLoadingBatchSize={25}
            rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
            lastRowBold={true}
            onSort={handleSort}
          />
        </Box>
      )}
    </Box>
  );
});

function NO2Report() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  const [ppoValue, setPpoValue] = useState('');
  const [ppoFilter, setPpoFilter] = useState('Inbound');
  const [fromYear, setFromYear] = useState('');
  const [fromQuarter, setFromQuarter] = useState('');
  const [toYear, setToYear] = useState('');
  const [toQuarter, setToQuarter] = useState('');

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<MailDataStructure | null>(null);
  // Add state to track when no data is found
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  // Add state to track the direction used for current results
  const [resultsDirection, setResultsDirection] = useState<string>('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Get effective operators and cascading logic for eno1 module
  const {
    operators,
    availableYears,
    operatorsForYearRange,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('eno1', { fromYear, toYear, selectedPpo: ppoValue || undefined });

  // NO2 Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError,
  } = useNO2Report();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = (fromYear || toYear) ? operatorsForYearRange : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYearRange, fromYear, toYear]);

  // Generate year-quarter options with cascading logic
  const yearQuarterOptions = React.useMemo(() => {
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const yearsToShow = ppoValue ? yearsForOperator : availableYears;
    yearsToShow.forEach((year) => {
      quarters.forEach((quarter) => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({ value, label, year: year.toString(), quarter });
      });
    });
    return options;
  }, [availableYears, yearsForOperator, ppoValue]);

  // Get current from and to values for display
  const getFromValue = () => {
    if (fromYear && fromQuarter) {
      return `${fromYear}-${fromQuarter}`;
    }
    return '';
  };

  const getToValue = () => {
    if (toYear && toQuarter) {
      return `${toYear}-${toQuarter}`;
    }
    return '';
  };

  // Handle from dropdown change
  const handleFromChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find((opt) => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (fromYear !== option.year && ppoValue) {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has eno1 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'eno1' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newFromYearNum = parseInt(option.year);
          
          // Check if the new from year is within the DO's range
          let shouldClearDO = false;
          
          if (toYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const toYearNum = parseInt(toYear);
            shouldClearDO = !(maxYear >= newFromYearNum && minYear <= toYearNum);
          } else {
            // Only from year is selected - check if DO has data from that year onwards
            shouldClearDO = maxYear < newFromYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('');
          }
        }
        
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  // Handle to dropdown change
  const handleToChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find((opt) => opt.value === value);
      if (option) {
        // Check if selected to date is earlier than current from date
        if (fromYear && fromQuarter) {
          const comparison = compareYearQuarter(fromYear, fromQuarter, option.year, option.quarter);
          if (comparison > 0) {
            showSnackbar('To date cannot be earlier than From date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (toYear !== option.year && ppoValue) {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has eno1 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'eno1' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newToYearNum = parseInt(option.year);
          
          // Check if the new to year is within the DO's range
          let shouldClearDO = false;
          
          if (fromYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const fromYearNum = parseInt(fromYear);
            shouldClearDO = !(maxYear >= fromYearNum && minYear <= newToYearNum);
          } else {
            // Only to year is selected - check if DO has data up to that year
            shouldClearDO = minYear > newToYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('');
          }
        }
        
        setToYear(option.year);
        setToQuarter(option.quarter);
      }
    } else {
      setToYear('');
      setToQuarter('');
    }
  };

  // Service Type state
  const [serviceType, setServiceType] = useState<ServiceTypeState>({
    aircraftPriority: true,
    salNonPriority: true,
    surfaceNonPriority: true,
    surfacePriority: true,
  });

  // Despatch state
  const [despatch, setDespatch] = useState<DespatchState>({
    letters: true,
    flats: true,
    packets: true,
    pdfManual: true,
    journals: true,
    bilateral: true,
  });

  // Accounting Type state
  const [accountingType, setAccountingType] = useState<AccountingTypeState>({
    gross: true,
    net: true,
  });

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change with cascading behavior
  const handleDOChange = (value: string) => {
    // Only clear year selection if the currently selected year range is not available for the new DO
    if (ppoValue !== value && (fromYear || toYear)) {
      // Check if the current year range is available for the new DO
      const relationsForCurrentDO = allRelationsData?.byTargetDO?.[value];
      if (!relationsForCurrentDO) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
        setPpoValue(value);
        return;
      }
      
      // Get years where this DO has eno1 module enabled
      const availableYears = relationsForCurrentDO
        .filter(relation => 
          relation.modules.some(module => 
            module.name === 'eno1' && module.enabled === true
          )
        )
        .map(relation => relation.year)
        .sort((a, b) => a - b);
      
      if (availableYears.length === 0) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
        setPpoValue(value);
        return;
      }
      
      const minYear = availableYears[0];
      const maxYear = availableYears[availableYears.length - 1];
      
      let shouldClearYears = false;
      
      if (fromYear && toYear) {
        // Both from and to are selected - check if the range overlaps with DO's range
        const fromYearNum = parseInt(fromYear);
        const toYearNum = parseInt(toYear);
        shouldClearYears = !(maxYear >= fromYearNum && minYear <= toYearNum);
      } else if (fromYear) {
        // Only from year is selected - check if DO has data from that year onwards
        const fromYearNum = parseInt(fromYear);
        shouldClearYears = maxYear < fromYearNum;
      } else if (toYear) {
        // Only to year is selected - check if DO has data up to that year
        const toYearNum = parseInt(toYear);
        shouldClearYears = minYear > toYearNum;
      }
      
      if (shouldClearYears) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
      }
    }
    setPpoValue(value);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleServiceTypeChange =
    (field: keyof ServiceTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setServiceType((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleDespatchChange =
    (field: keyof DespatchState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setDespatch((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleAccountingTypeChange =
    (field: keyof AccountingTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setAccountingType((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleSelectAllServiceType = () => {
    const allSelected =
      serviceType.aircraftPriority &&
      serviceType.salNonPriority &&
      serviceType.surfaceNonPriority &&
      serviceType.surfacePriority;

    if (allSelected) {
      // Deselect all
      setServiceType({
        aircraftPriority: false,
        salNonPriority: false,
        surfaceNonPriority: false,
        surfacePriority: false,
      });
    } else {
      // Select all
      setServiceType({
        aircraftPriority: true,
        salNonPriority: true,
        surfaceNonPriority: true,
        surfacePriority: true,
      });
    }
  };

  const handleSelectAllDespatch = () => {
    const allSelected =
      despatch.letters &&
      despatch.flats &&
      despatch.packets &&
      despatch.pdfManual &&
      despatch.journals &&
      despatch.bilateral;

    if (allSelected) {
      // Deselect all
      setDespatch({
        letters: false,
        flats: false,
        packets: false,
        pdfManual: false,
        journals: false,
        bilateral: false,
      });
    } else {
      // Select all
      setDespatch({
        letters: true,
        flats: true,
        packets: true,
        pdfManual: true,
        journals: true,
        bilateral: true,
      });
    }
  };

  const handleSelectAllAccountingType = () => {
    const allSelected = accountingType.gross && accountingType.net;

    if (allSelected) {
      // Deselect all
      setAccountingType({
        gross: false,
        net: false,
      });
    } else {
      // Select all
      setAccountingType({
        gross: true,
        net: true,
      });
    }
  };

  const handleReset = () => {
    setPpoValue('');
    setPpoFilter('Inbound');
    setFromYear('');
    setFromQuarter('');
    setToYear('');
    setToQuarter('');
    setServiceType({
      aircraftPriority: true,
      salNonPriority: true,
      surfaceNonPriority: true,
      surfacePriority: true,
    });
    setDespatch({
      letters: true,
      flats: true,
      packets: true,
      pdfManual: true,
      journals: true,
      bilateral: true,
    });
    setAccountingType({
      gross: true,
      net: true,
    });
    // Clear no data found state and results when resetting
    setNoDataFound(false);
    setHtmlResults(null);
    setResultsDirection('');
  };

  // Map checkbox states to API parameter values
  const getServiceTypeValues = (): string[] => {
    const values: string[] = [];
    if (serviceType.aircraftPriority) values.push('A');
    if (serviceType.salNonPriority) values.push('B');
    if (serviceType.surfaceNonPriority) values.push('C');
    if (serviceType.surfacePriority) values.push('D');
    return values;
  };

  const getMailFormatValues = (): string[] => {
    const values: string[] = [];
    if (despatch.letters) values.push('P');
    if (despatch.flats) values.push('G');
    if (despatch.packets) values.push('E');
    if (despatch.pdfManual) values.push('X');
    if (despatch.journals) values.push('J');
    if (despatch.bilateral) values.push('Z');
    return values;
  };

  const getAccountingTypeValues = (): string[] => {
    const values: string[] = [];
    if (accountingType.gross) values.push('G');
    if (accountingType.net) values.push('N');
    return values;
  };

  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    } else {
      newParams.append('ppo', 'All');
    }

    // Map from year if available
    if (fromYear) {
      newParams.append('year', fromYear);
    } else {
      newParams.append('year', 'all');
    }

    // Add custom NO2Report-specific filters
    const serviceTypes = getServiceTypeValues();
    const mailFormats = getMailFormatValues();
    const accountingTypes = getAccountingTypeValues();

    if (serviceTypes.length > 0) {
      newParams.append('serviceTypes', serviceTypes.join(','));
    }

    if (mailFormats.length > 0) {
      newParams.append('mailFormats', mailFormats.join(','));
    }

    if (accountingTypes.length > 0) {
      newParams.append('accountingTypes', accountingTypes.join(','));
    }

    if (fromYear && fromQuarter) {
      newParams.append('fromPeriod', `${fromYear}-${fromQuarter}`);
    }

    if (toYear && toQuarter) {
      newParams.append('toPeriod', `${toYear}-${toQuarter}`);
    }

    setSearchParams(newParams);
  };

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields - From and To are now required
    if (!fromYear || !fromQuarter) {
      showSnackbar('Please select a From period (Year and Quarter)');
      return;
    }

    if (!toYear || !toQuarter) {
      showSnackbar('Please select a To period (Year and Quarter)');
      return;
    }

    // Validate required fields
    const serviceTypes = getServiceTypeValues();
    const mailFormats = getMailFormatValues();
    const accountingTypes = getAccountingTypeValues();

    if (serviceTypes.length === 0) {
      showSnackbar('Please select at least one Service Type');
      return;
    }

    if (mailFormats.length === 0) {
      showSnackbar('Please select at least one Despatch option');
      return;
    }

    if (accountingTypes.length === 0) {
      showSnackbar('Please select at least one Accounting Type');
      return;
    }

    // Prepare API parameters
    const params = {
      serviceTypes,
      mailFormats,
      accountingTypes,
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      fromYear: parseInt(fromYear),
      fromQuarter: parseInt(fromQuarter.replace('Q', '')),
      toYear: parseInt(toYear),
      toQuarter: parseInt(toQuarter.replace('Q', '')),
      ...(ppoValue && { designatedOperator: ppoValue }),
      ...(ppoFilter && { table: ppoFilter }),
    };

    generateReport(params, {
      onSuccess: (response) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          console.log('HTML Report tables generated:', response.data);
          console.log('Current filter params used:', params);
          
          // Check for 204 No Content or empty data
          if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
            // Set no data found state instead of clearing results
            setNoDataFound(true);
            setHtmlResults(null);
            // Don't apply filters to SectionBar when no data is found
            return;
          }

          if (response.data) {
            // Store the data in a variable to ensure type safety
            const newData: MailDataStructure = response.data;
            // Clear no data found state and set new data
            setNoDataFound(false);
            setHtmlResults(null);
            // Store the direction used for this report
            setResultsDirection(ppoFilter);
            // Use setTimeout to ensure the state is cleared before setting new data
            setTimeout(() => {
              setHtmlResults(newData);
              console.log('Updated htmlResults state with new data:', newData);
            }, 0);
            // Apply current filters to SectionBar only when data is found
            applyFiltersToSectionBar();
          }
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(
            `${format} Report downloaded successfully with filename: ${response.filename}`
          );
        }
      },
      onError: async (error) => {
        console.error('Error generating report:', error);
        
        let errorMessage = 'An error occurred while generating the report';
        let errorStatus: string | undefined;
        
        // Check if it's an axios error
        if (axios.isAxiosError(error)) {
          // For blob responses, the error data might be a Blob containing JSON
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            } catch (parseError) {
              // If parsing fails, use default message
              console.error('Failed to parse error response:', parseError);
            }
          } else if (error.response?.data && typeof error.response.data === 'object') {
            // For non-blob responses, check the data directly
            const errorData = error.response.data as { message?: string; status?: string };
            errorMessage = errorData.message || errorMessage;
            errorStatus = errorData.status;
          }
          
          // Check status code as fallback
          if (!errorStatus && error.response?.status === 404) {
            errorStatus = 'NOT_FOUND';
          }
        }
        
        // Show snackbar with appropriate message for all formats (HTML, PDF, XLS)
        if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No ENO2 reports available')) {
          showSnackbar(
            errorMessage || 'No ENO2 reports available for the selected criteria',
            'warning'
          );
        } else {
          showSnackbar(errorMessage, 'error');
        }
      },
    });
  };

  // Memoize mailColumns to prevent recreation on every render
  const mailColumns = useMemo(() => createMailColumns(), []);

  // Use ref to track if we've already synced URL parameters
  const hasSyncedUrlParams = useRef(false);

  // Initial URL parameter sync when page loads
  useEffect(() => {
    // Only sync once and when yearQuarterOptions are available
    if (hasSyncedUrlParams.current || yearQuarterOptions.length === 0) {
      return;
    }

    const ppoParam = searchParams.get('ppo');
    const fromYearParam = searchParams.get('fromYear');
    const toYearParam = searchParams.get('toYear');
    const fromQuarterParam = searchParams.get('fromQuarter');
    const toQuarterParam = searchParams.get('toQuarter');
    const tableParam = searchParams.get('table');
    const fromPeriodParam = searchParams.get('fromPeriod');
    const toPeriodParam = searchParams.get('toPeriod');

    // Sync PPO parameter
    if (ppoParam) {
      setPpoValue(ppoParam === 'All' ? '' : ppoParam);
    }

    // Sync direction filter from table parameter
    if (tableParam) {
      setPpoFilter(tableParam === 'inbound' ? 'Inbound' : 'Outbound');
    }

    // Sync from period parameters - check both individual params and combined period param
    if (fromYearParam && fromQuarterParam) {
      setFromYear(fromYearParam);
      setFromQuarter(`Q${fromQuarterParam}`);
    } else if (fromPeriodParam) {
      const option = yearQuarterOptions.find((opt) => opt.value === fromPeriodParam);
      if (option) {
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    }

    // Sync to period parameters - check both individual params and combined period param
    if (toYearParam && toQuarterParam) {
      setToYear(toYearParam);
      setToQuarter(`Q${toQuarterParam}`);
    } else if (toPeriodParam) {
      const option = yearQuarterOptions.find((opt) => opt.value === toPeriodParam);
      if (option) {
        setToYear(option.year);
        setToQuarter(option.quarter);
      }
    }

    // Mark as synced
    hasSyncedUrlParams.current = true;
  }, [searchParams, yearQuarterOptions]); // Depend on yearQuarterOptions to ensure they're available

  // Sync URL params with page state when SectionBar filters change (only when results exist)
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');
    const yearParam = searchParams.get('year');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults) {
      if (ppoParam && ppoParam !== ppoValue) {
        setPpoValue(ppoParam === 'All' ? '' : ppoParam);
      }

      if (yearParam && yearParam !== 'all' && yearParam !== fromYear) {
        setFromYear(yearParam);
      }
    }
  }, [searchParams, htmlResults, ppoValue, fromYear]);



  // Remove auto-regeneration - users must click export buttons manually

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    setNoDataFound(false);
    setResultsDirection('');
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Helper functions to determine button text
  const getServiceTypeButtonText = () => {
    const allSelected =
      serviceType.aircraftPriority &&
      serviceType.salNonPriority &&
      serviceType.surfaceNonPriority &&
      serviceType.surfacePriority;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getDespatchButtonText = () => {
    const allSelected =
      despatch.letters &&
      despatch.flats &&
      despatch.packets &&
      despatch.pdfManual &&
      despatch.journals &&
      despatch.bilateral;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getAccountingTypeButtonText = () => {
    const allSelected = accountingType.gross && accountingType.net;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarServiceTypesChange = useCallback((values: string[]) => {
    setServiceType({
      aircraftPriority: values.includes('A'),
      salNonPriority: values.includes('B'),
      surfaceNonPriority: values.includes('C'),
      surfacePriority: values.includes('D'),
    });
  }, []);

  const handleSectionBarMailFormatsChange = useCallback((values: string[]) => {
    setDespatch({
      letters: values.includes('P'),
      flats: values.includes('G'),
      packets: values.includes('E'),
      pdfManual: values.includes('X'),
      journals: values.includes('J'),
      bilateral: values.includes('Z'),
    });
  }, []);

  const handleSectionBarAccountingTypesChange = useCallback((values: string[]) => {
    setAccountingType({
      gross: values.includes('G'),
      net: values.includes('N'),
    });
  }, []);

  const handleSectionBarFromPeriodChange = useCallback(
    (period: string) => {
      if (period) {
        const option = yearQuarterOptions.find((opt) => opt.value === period);
        if (option) {
          setFromYear(option.year);
          setFromQuarter(option.quarter);
        }
      } else {
        setFromYear('');
        setFromQuarter('');
      }
    },
    [yearQuarterOptions]
  );

  const handleSectionBarToPeriodChange = useCallback(
    (period: string) => {
      if (period) {
        const option = yearQuarterOptions.find((opt) => opt.value === period);
        if (option) {
          setToYear(option.year);
          setToQuarter(option.quarter);
        }
      } else {
        setToYear('');
        setToQuarter('');
      }
    },
    [yearQuarterOptions]
  );

  // Handler function for SectionBar direction change
  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setPpoFilter(direction);
  }, []);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    // Only show SectionBar custom filters when we have actual results (not when no data found)
    if (!htmlResults || noDataFound) return undefined;

    return {
      serviceTypes: getServiceTypeValues(),
      mailFormats: getMailFormatValues(),
      accountingTypes: getAccountingTypeValues(),
      fromPeriod: getFromValue(),
      toPeriod: getToValue(),
      direction: ppoFilter,
      yearQuarterOptions: yearQuarterOptions, // Pass cascaded year-quarter options
      // Pass cascaded PPO options and current PPO value for SectionBar
      ppo: ppoValue || 'All',
      ppoOptions: ppoOptions.map(option => ({
        id: option.id,
        name: option.label,
        disabled: false
      })),
      isPpoLoading: isPpoLoading,
      onServiceTypesChange: handleSectionBarServiceTypesChange,
      onMailFormatsChange: handleSectionBarMailFormatsChange,
      onAccountingTypesChange: handleSectionBarAccountingTypesChange,
      onFromPeriodChange: handleSectionBarFromPeriodChange,
      onToPeriodChange: handleSectionBarToPeriodChange,
      onDirectionChange: handleSectionBarDirectionChange,
      onPpoChange: handleDOChange,
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    noDataFound,
    serviceType,
    despatch,
    accountingType,
    fromYear,
    fromQuarter,
    toYear,
    toQuarter,
    ppoFilter,
    yearQuarterOptions,
    ppoValue,
    ppoOptions,
    isPpoLoading,
    getFromValue,
    getToValue,
    handleSectionBarServiceTypesChange,
    handleSectionBarMailFormatsChange,
    handleSectionBarAccountingTypesChange,
    handleSectionBarFromPeriodChange,
    handleSectionBarToPeriodChange,
    handleSectionBarDirectionChange,
    handleDOChange,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='no2-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='NO2 Report'
            subtitle='eNO1 Sampling'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            showFilters={{
              ppo: !!htmlResults && !noDataFound,
              customNO2: !!htmlResults && !noDataFound,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="no2-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='no2-report-main-content'>
            <ReportContainer data-testid="no2-report-content">
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid="no2-report-header"
              >
                <Typography variant='h4' sx={{ fontWeight: 600, color: '#063142' }} data-testid="no2-report-title">
                  NO2 Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="no2-report-header-actions">
                  {(htmlResults || noDataFound) && (
                    <ClearResultsButton variant='outlined' onClick={clearResults} data-testid="no2-report-clear-results-btn">
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Main Filter Section - Hide when HTML results exist (but show when no data found) */}
              {!htmlResults && (
                <>
                  {/* PPO Section */}
                  <FilterSection>
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                            >
                              <MenuItem value=''>
                                {isPpoLoading ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                ) : (
                                  'All'
                                )}
                              </MenuItem>
                              {!isPpoLoading &&
                                !ppoError &&
                                ppoOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                      }}
                                    >
                                      <span>{option.label}</span>
                                    </Box>
                                  </MenuItem>
                                ))}
                              {ppoError && <MenuItem disabled>Error loading PPOs</MenuItem>}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl>
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px',
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        mb: 2,
                      }}
                    />

                    {/* Date Selectors with titles */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          From YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={getFromValue()}
                              onChange={(e) => handleFromChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value=''>Select</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          To YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={getToValue()}
                              onChange={(e) => handleToChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value=''>Select</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                  />

                  {/* Main Content Grid - without borders, with dividers */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      width: '100%',
                    }}
                  >
                    {/* Service Type */}
                    <Box
                      sx={{
                        flex: { xs: 'none', md: '1 1 0' },
                        padding: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <SectionTitle>Service Type</SectionTitle>
                        <Link
                          component='button'
                          onClick={handleSelectAllServiceType}
                          sx={{
                            color: '#00ACE7',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none',
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC',
                            },
                          }}
                        >
                          {getServiceTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={serviceType.aircraftPriority}
                          onChange={handleServiceTypeChange('aircraftPriority')}
                          label='A - Airmail Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.salNonPriority}
                          onChange={handleServiceTypeChange('salNonPriority')}
                          label='B - SAL Non-Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.surfaceNonPriority}
                          onChange={handleServiceTypeChange('surfaceNonPriority')}
                          label='C - Surface Non-Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.surfacePriority}
                          onChange={handleServiceTypeChange('surfacePriority')}
                          label='D - Surface Priority'
                        />
                      </CheckboxGroup>
                    </Box>

                    {/* Vertical Divider */}
                    <Box
                      sx={{
                        width: '1px',
                        backgroundColor: '#E1E8ED',
                        display: { xs: 'none', md: 'block' },
                      }}
                    />

                    {/* Horizontal Divider for mobile */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        my: 2,
                        display: { xs: 'block', md: 'none' },
                      }}
                    />

                    {/* Despatch */}
                    <Box
                      sx={{
                        flex: { xs: 'none', md: '1 1 0' },
                        padding: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <SectionTitle>Despatch</SectionTitle>
                        <Link
                          component='button'
                          onClick={handleSelectAllDespatch}
                          sx={{
                            color: '#00ACE7',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none',
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC',
                            },
                          }}
                        >
                          {getDespatchButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={despatch.letters}
                          onChange={handleDespatchChange('letters')}
                          label='P - Letters'
                        />
                        <CustomCheckbox
                          checked={despatch.flats}
                          onChange={handleDespatchChange('flats')}
                          label='G - Flats'
                        />
                        <CustomCheckbox
                          checked={despatch.packets}
                          onChange={handleDespatchChange('packets')}
                          label='E - Packets'
                        />
                        <CustomCheckbox
                          checked={despatch.pdfManual}
                          onChange={handleDespatchChange('pdfManual')}
                          label='X - PGE Mixed'
                        />
                        <CustomCheckbox
                          checked={despatch.journals}
                          onChange={handleDespatchChange('journals')}
                          label='J - Journals'
                        />
                        <CustomCheckbox
                          checked={despatch.bilateral}
                          onChange={handleDespatchChange('bilateral')}
                          label='Z - Bilateral'
                        />
                      </CheckboxGroup>
                    </Box>

                    {/* Vertical Divider */}
                    <Box
                      sx={{
                        width: '1px',
                        backgroundColor: '#E1E8ED',
                        display: { xs: 'none', md: 'block' },
                      }}
                    />

                    {/* Horizontal Divider for mobile */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        my: 2,
                        display: { xs: 'block', md: 'none' },
                      }}
                    />

                    {/* Accounting Type */}
                    <Box
                      sx={{
                        flex: { xs: 'none', md: '1 1 0' },
                        padding: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <SectionTitle>Accounting Type</SectionTitle>
                        <Link
                          component='button'
                          onClick={handleSelectAllAccountingType}
                          sx={{
                            color: '#00ACE7',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none',
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC',
                            },
                          }}
                        >
                          {getAccountingTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={accountingType.gross}
                          onChange={handleAccountingTypeChange('gross')}
                          label='Gross'
                        />
                        <CustomCheckbox
                          checked={accountingType.net}
                          onChange={handleAccountingTypeChange('net')}
                          label='Net'
                        />
                      </CheckboxGroup>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                    <ResetButton variant='contained' onClick={handleReset}>
                      RESET
                    </ResetButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('HTML')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('PDF')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('XLS')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                    </ExportButton>
                  </ButtonContainer>
                </>
              )}

              {/* No Data Found Section */}
              {noDataFound && (
                <NoDataFound
                  title="No Data Available"
                  message="No NO2 report data was found for the selected criteria. Please try adjusting your filters or selecting a different date range."
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                />
              )}

              {/* Results Section - Show tables directly under filters */}
              {htmlResults && !noDataFound && (
                <ResultsSection htmlResults={htmlResults} mailColumns={mailColumns} direction={resultsDirection} />
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='no2-report-snackbar'
      />
    </Box>
  );
}

export default NO2Report;
